 
 
 
 
 
 
/**
 * This widget displays a panel with route instructions over the map.
 * 
 * This widget requires route data to work correcly. For more information please refer to our 
 * <a href="https://developer.tomtom.com/maps-sdk-web/functional-examples#routing_ab_instructions" target="_blank">
 * Routing example</a>.
 *
 * @class RouteInstructions
 * @namespace L
 * @extends L.Control
 * @module Widgets
 * @constructor
 * @param {Object} [options] Options to configure the widget
 * @param {Number[]} [options.size=[320, 320]] Size of panel. The first element of the array is the width
 *     and the second the maximum height. It will fit to the size of the instructions.
 * @param {"topright"|"topleft"|"bottomright"|"bottomleft"} [options.position="topleft"] Location of the
 *     widget
 * @param {Boolean} [options.instructionGroupsCollapsed=false] Should instruction groups be collapsed on init
 * @param {Boolean} [options.showInstructionGroups=true] Should instruction groups be displayed
 * @param {Boolean} [options.imperialDistance=null] Set this option if you want to use a specific unit
 *     measurement system for the new instance of the widget instead of use the default unit
 *
 * @example
 * ```javascript
 * var routeInstructionsInstance = new L.RouteInstructions({
 *     size: [270, 268],
 *     position: "topleft"
 * })
 * routeInstructionsInstance.addTo(map);
 * ```
 */
/* jshint nomen:false */
define(['display/map', 'unitFormatConverter/unitFormatConverter', '../../config/unit/unitTypes', 'services/utils'],
    function(L, unitFormatConverter, unitTypes, Utils) {
        'use strict';

        var EVENTS = {
                InstructionClickedSelect: 'instructionClickedSelect',
                InstructionClickedDeselect: 'instructionClickedDeselect',
                InstructionHoverOn: 'instructionHoverOn',
                InstructionHoverOff: 'instructionHoverOff',
                InstructionGroupClickedCollapse: 'instructionGroupClickedCollapse',
                InstructionGroupClickedExpand: 'instructionGroupClickedExpand',
                InstructionGroupHoverOn: 'instructionGroupHoverOn',
                InstructionGroupHoverOff: 'instructionGroupHoverOff'
            },

            CSS_CLASS_NAME = 'leaflet-control-route-instructions',

            MANOUVERS_WITH_LEFT_DRIVING_SIDE_VERSIONS = ['MAKE_UTURN', 'TRY_MAKE_UTURN', 'ROUNDABOUT_RIGHT',
                'ROUNDABOUT_LEFT', 'ROUNDABOUT_CROSS', 'ROUNDABOUT_BACK'
            ],

            iconsMap = {
                'ARRIVE': 'icon-end-black',
                'ARRIVE_LEFT': 'icon-end-black',
                'ARRIVE_RIGHT': 'icon-end-black',
                'DEPART': 'icon-start-black',
                'STRAIGHT': 'icon-follow',
                'KEEP_RIGHT': 'icon-keep_right',
                'BEAR_RIGHT': 'icon-bear_right',
                'TURN_RIGHT': 'icon-turn_right',
                'SHARP_RIGHT': 'icon-sharp_right',
                'KEEP_LEFT': 'icon-keep_left',
                'BEAR_LEFT': 'icon-bear_left',
                'TURN_LEFT': 'icon-turn_left',
                'SHARP_LEFT': 'icon-sharp_left',
                'MAKE_UTURN': 'icon-make_uturn',
                'MAKE_UTURN_LEFT_DRIVING_SIDE': 'icon-make_uturn_left_driving_side',
                'ENTER_MOTORWAY': 'icon-freeway',
                'ENTER_FREEWAY': 'icon-freeway',
                'ENTER_HIGHWAY': 'icon-freeway',
                'TAKE_EXIT': 'icon-follow',
                'MOTORWAY_EXIT_LEFT': 'icon-keep_left',
                'MOTORWAY_EXIT_RIGHT': 'icon-keep_right',
                'TAKE_FERRY': 'icon-ferry',
                'ROUNDABOUT_CROSS': 'icon-roundabout_cross',
                'ROUNDABOUT_CROSS_45': 'icon-roundabout_cross_45',
                'ROUNDABOUT_RIGHT_135': 'icon-roundabout_right_135',
                'ROUNDABOUT_CROSS_MINUS_45': 'icon-roundabout_cross_minus_45',
                'ROUNDABOUT_LEFT_MINUS_135': 'icon-roundabout_left_minus_135',
                'ROUNDABOUT_RIGHT': 'icon-roundabout_right',
                'ROUNDABOUT_LEFT': 'icon-roundabout_left',
                'ROUNDABOUT_BACK': 'icon-roundabout_back',
                'TRY_MAKE_UTURN': 'make_uturn',
                'TRY_MAKE_UTURN_LEFT_DRIVING_SIDE': 'icon-make_uturn_left_driving_side',
                'ROUNDABOUT_CROSS_LEFT_DRIVING_SIDE': 'icon-roundabout_cross_left_driving_side',
                'ROUNDABOUT_CROSS_45_LEFT_DRIVING_SIDE': 'icon-roundabout_cross_45_left_driving_side',
                'ROUNDABOUT_RIGHT_135_LEFT_DRIVING_SIDE': 'icon-roundabout_right_135_left_driving_side',
                'ROUNDABOUT_CROSS_MINUS_45_LEFT_DRIVING_SIDE': 'icon-roundabout_cross_minus_45_left_driving_side',
                'ROUNDABOUT_LEFT_MINUS_135_LEFT_DRIVING_SIDE': 'icon-roundabout_left_minus_135_left_driving_side',
                'ROUNDABOUT_RIGHT_LEFT_DRIVING_SIDE': 'icon-roundabout_right_left_driving_side',
                'ROUNDABOUT_LEFT_LEFT_DRIVING_SIDE': 'icon-roundabout_left_left_driving_side',
                'ROUNDABOUT_BACK_LEFT_DRIVING_SIDE': 'icon-roundabout_back_left_driving_side',
                'FOLLOW': 'icon-follow',
                'WAYPOINT_LEFT': 'icon-flag-black',
                'WAYPOINT_RIGHT': 'icon-flag-black',
                'WAYPOINT_REACHED': 'icon-flag-black'
            };

        /**
         * @ignore
         * @param {HTMLElement} control
         * @return {HTMLElement}
         */
        function createDOMElements(control) {
            var container = L.DomUtil.create('div', CSS_CLASS_NAME + ' leaflet-control');
            control.container = container;
            container.style.width = control.options.size[0] + 'px';
            container.style.maxHeight = control.options.size[1] + 'px';

            L.DomEvent.disableClickPropagation(container);
            L.DomEvent.disableScrollPropagation(container);

            container.style.visibility = 'hidden';

            return container;
        }

        /**
         * @ignore
         * @param {HTMLElement} control
         */
        function clearDOMElements(control) {
            control.container.innerHTML = '';
        }

        function createCollapseIconDiv(isInstructionGroupsCollapsed) {
            var collapseIconClass = isInstructionGroupsCollapsed ? 'icon-expand' : 'icon-collapse';
            return L.DomUtil.create('div', CSS_CLASS_NAME + '-collapse-icon ' + collapseIconClass);
        }

        function createGroupMessageDiv(instructionGroup) {
            var groupMessageDiv = L.DomUtil.create('div', CSS_CLASS_NAME + '-group-message');
            groupMessageDiv.innerHTML = instructionGroup.groupMessage;
            return groupMessageDiv;
        }

        function createSeparatorDiv(divToSeparate, instructionDistance, isImperialDistance, separatorCssClass,
            isInstructionGroup) {

            var separatorDistanceDiv,
                autoLengthSeparatorDiv,
                instructionSeparatorDiv,
                distanceDiv;
            if (instructionDistance === 0) {
                return;
            }
            separatorDistanceDiv = L.DomUtil.create('div', CSS_CLASS_NAME + '-separator-distance', divToSeparate);
            if (isInstructionGroup) {
                L.DomUtil.addClass(separatorDistanceDiv, CSS_CLASS_NAME + '-group-separator-distance');
            }
            autoLengthSeparatorDiv = L.DomUtil.create(
                'div', CSS_CLASS_NAME + '-auto-length-separator', separatorDistanceDiv);
            instructionSeparatorDiv = L.DomUtil.create('div', CSS_CLASS_NAME + '-separator', autoLengthSeparatorDiv);
            L.DomUtil.addClass(instructionSeparatorDiv, separatorCssClass);
            distanceDiv = L.DomUtil.create('div', CSS_CLASS_NAME + '-distance', separatorDistanceDiv);
            distanceDiv.innerHTML = unitFormatConverter.formatDistance(instructionDistance, isImperialDistance);
        }

        function getIconPathKey(instruction) {
            var iconKey = instruction.maneuver;
            if (instruction.maneuver === 'ROUNDABOUT_CROSS') {
                if (instruction.turnAngleInDecimalDegrees >= 22 && instruction.turnAngleInDecimalDegrees <= 68) {
                    iconKey += '_45';
                } else if (instruction.turnAngleInDecimalDegrees <= -22 &&
                        instruction.turnAngleInDecimalDegrees >= -68) {
                    iconKey += '_MINUS_45';
                }
            }
            if (instruction.maneuver === 'ROUNDABOUT_RIGHT' && instruction.turnAngleInDecimalDegrees >= 122 &&
                instruction.turnAngleInDecimalDegrees <= 158) {
                iconKey += '_135';
            }
            if (instruction.maneuver === 'ROUNDABOUT_LEFT' && instruction.turnAngleInDecimalDegrees >= -158 &&
                instruction.turnAngleInDecimalDegrees <= -122) {
                iconKey += '_MINUS_135';
            }
            if (instruction.drivingSide === 'LEFT' &&
                MANOUVERS_WITH_LEFT_DRIVING_SIDE_VERSIONS.indexOf(instruction.maneuver) !== -1) {
                iconKey += '_LEFT_DRIVING_SIDE';
            }
            return iconKey;
        }

        function createInstructionElement(instruction) {
            var messageDiv;
            var instructionElementDiv = L.DomUtil.create('div', CSS_CLASS_NAME + '-instruction-element');
            var messageWithIconDiv = L.DomUtil.create('div',
                CSS_CLASS_NAME + '-message-with-icon', instructionElementDiv);
            L.DomUtil.create('div', CSS_CLASS_NAME + '-icon ' +
                iconsMap[getIconPathKey(instruction)], messageWithIconDiv);
            messageDiv = L.DomUtil.create('div', CSS_CLASS_NAME + '-message', messageWithIconDiv);
            messageDiv.innerHTML = instruction.message;
            return instructionElementDiv;
        }

        function createEventObject(instructionPoints, instructionMessage) {
            var eventObject = {};
            if (instructionPoints.constructor.toString().indexOf('Array') > -1) {
                eventObject.points = instructionPoints;
            } else {
                eventObject.point = instructionPoints;
            }
            if (instructionMessage) {
                eventObject.message = instructionMessage;
            }

            return eventObject;
        }

        function toggleCssClasses(htmlElement, cssClassesArray) {
            cssClassesArray.forEach(function(className) {
                if (L.DomUtil.hasClass(htmlElement, className)) {
                    L.DomUtil.removeClass(htmlElement, className);
                } else {
                    L.DomUtil.addClass(htmlElement, className);
                }
            });
        }

        function deselectInstruction(container, selectedInstructionClass, control) {
            var selectedInstruction = container.getElementsByClassName(selectedInstructionClass)[0];
            if (selectedInstruction) {
                L.DomUtil.removeClass(selectedInstruction, selectedInstructionClass);
                control.fire(control.Events.InstructionClickedDeselect);
            }
        }

        function registerHandlersForInstruction(control, instructionElement, instruction) {
            L.DomEvent.on(instructionElement, 'mouseenter', function() {
                control.fire(control.Events.InstructionHoverOn,
                    createEventObject(instruction.point, instruction.message));
            });
            L.DomEvent.on(instructionElement, 'mouseleave', function() {
                control.fire(control.Events.InstructionHoverOff);
            });
            L.DomEvent.on(instructionElement, 'click', function() {
                var selectedInstructionClass;
                control.container.focus();
                selectedInstructionClass = ['selected-instruction'];

                if (L.DomUtil.hasClass(instructionElement, selectedInstructionClass)) {
                    control.fire(control.Events.InstructionClickedDeselect);
                } else {
                    deselectInstruction(control.container, selectedInstructionClass, control);
                    control.fire(
                        control.Events.InstructionClickedSelect,
                        createEventObject(instruction.point, instruction.message)
                    );
                }

                toggleCssClasses(instructionElement, selectedInstructionClass);
            });
        }

        function createInstructions(control, divForInstructions, instructionList, instructionNextToGroup) {
            var nextInstructionOffset = 0;
            var instructionPoints = [];
            var instructionIndex,
                currentInstruction,
                nextInstruction,
                instructionElementDiv,
                separatorCssClassName,
                isImperialDistance = control._shouldUseImperialDistance();

            for (instructionIndex = 0; instructionIndex < instructionList.length; instructionIndex += 1) {
                currentInstruction = instructionList[instructionIndex];
                instructionPoints.push(currentInstruction.point);
                nextInstruction = instructionList[instructionIndex + 1];
                instructionElementDiv = createInstructionElement(currentInstruction);
                divForInstructions.appendChild(instructionElementDiv);
                separatorCssClassName = 'separator-between-instructions';

                if (nextInstruction !== undefined) {
                    nextInstructionOffset = nextInstruction.routeOffsetInMeters;
                } else if (instructionNextToGroup) {
                    nextInstructionOffset = instructionNextToGroup.routeOffsetInMeters;
                    separatorCssClassName = 'separator-end-group';
                }

                createSeparatorDiv(instructionElementDiv,
                    nextInstructionOffset - currentInstruction.routeOffsetInMeters,
                    isImperialDistance, separatorCssClassName, false);
                registerHandlersForInstruction(control, instructionElementDiv, currentInstruction);
            }

            return instructionPoints;
        }

        function registerHandlersForGroup(params) {
            L.DomEvent.on(params.groupElementDiv, 'mouseenter', function() {
                params.control.fire(
                    params.control.Events.InstructionGroupHoverOn,
                    createEventObject(params.groupedInstructionPoints, params.groupMessage)
                );
            });
            L.DomEvent.on(params.groupElementDiv, 'mouseleave', function() {
                params.control.fire(params.control.Events.InstructionGroupHoverOff);
            });
            L.DomEvent.on(params.groupElementDiv, 'click', function() {
                var separator;
                params.control.container.focus();
                separator = params.groupElementDiv.getElementsByClassName(
                    'leaflet-control-route-instructions-separator'
                )[0];
                toggleCssClasses(separator, ['separator-between-instructions', 'separator-end-group']);
                toggleCssClasses(params.collapseIconDiv, ['icon-collapse', 'icon-expand']);

                if (params.groupedInstructionsDiv.style.display === 'none') {
                    params.groupedInstructionsDiv.style.display = '';
                    params.control.fire(
                        params.control.Events.InstructionGroupClickedExpand,
                        createEventObject(params.groupedInstructionPoints, null)
                    );
                } else {
                    params.groupedInstructionsDiv.style.display = 'none';
                    params.control.fire(
                        params.control.Events.InstructionGroupClickedCollapse,
                        createEventObject(params.allInstructionsPoints, null)
                    );
                }
            });
        }

        /**
         * @ignore
         * @param {HTMLElement} control
         * @param {Object} guidance
         */
        function createInstructionsWithGroups(control, guidance) {
            var container = control.container;
            var allInstructionsPoints = [];
            var instructionIndex,
                groupIndex,
                instructionGroup,
                groupElementDiv,
                groupedInstructionsDiv,
                groupMessageWithIconDiv,
                collapseIconDiv,
                groupMessageDiv,
                separatorCssClassName,
                instructionListForGroup,
                groupedInstructionPoints,
                isImperialDistance = control._shouldUseImperialDistance();

            for (instructionIndex = 0; instructionIndex < guidance.instructions.length; instructionIndex += 1) {
                allInstructionsPoints.push(guidance.instructions[instructionIndex].point);
            }

            for (groupIndex = 0; groupIndex < guidance.instructionGroups.length; groupIndex += 1) {
                instructionGroup = guidance.instructionGroups[groupIndex];
                groupElementDiv = L.DomUtil.create('div', CSS_CLASS_NAME + '-group-element', container);
                groupedInstructionsDiv = L.DomUtil.create('div', CSS_CLASS_NAME + '-grouped-instructions', container);

                groupMessageWithIconDiv = L.DomUtil.create(
                    'div', CSS_CLASS_NAME + '-group-message-with-icon', groupElementDiv);
                collapseIconDiv = createCollapseIconDiv(control.options.instructionGroupsCollapsed);
                groupMessageDiv = createGroupMessageDiv(instructionGroup);
                groupMessageWithIconDiv.appendChild(collapseIconDiv);
                groupMessageWithIconDiv.appendChild(groupMessageDiv);

                separatorCssClassName = control.options.instructionGroupsCollapsed ?
                    'separator-end-group' : 'separator-between-instructions';
                createSeparatorDiv(groupElementDiv, instructionGroup.groupLengthInMeters, isImperialDistance,
                    separatorCssClassName, true);

                instructionListForGroup = guidance.instructions.slice(instructionGroup.firstInstructionIndex,
                    instructionGroup.lastInstructionIndex + 1);
                groupedInstructionPoints = createInstructions(control, groupedInstructionsDiv, instructionListForGroup,
                    guidance.instructions[instructionGroup.lastInstructionIndex + 1]);

                registerHandlersForGroup({
                    control: control,
                    groupElementDiv: groupElementDiv,
                    groupMessage: instructionGroup.groupMessage,
                    groupedInstructionPoints: groupedInstructionPoints,
                    collapseIconDiv: collapseIconDiv,
                    groupedInstructionsDiv: groupedInstructionsDiv,
                    allInstructionsPoints: allInstructionsPoints
                });

                if (control.options.instructionGroupsCollapsed) {
                    groupedInstructionsDiv.style.display = 'none';
                }
            }
        }

        /**
         * @ignore
         * @param {HTMLElement} control
         */
        function updateDOMElements(control) {
            clearDOMElements(control);
            if (control.options.showInstructionGroups) {
                createInstructionsWithGroups(control, control.guidanceData);
            } else {
                createInstructions(control, control.container, control.guidanceData.instructions, null);
            }
        }

        function handleUnitChange(event) {
            this.options.imperialDistance = event.unit === 'imperial';
            this._redraw(true);
        }

        L.RouteInstructions = L.Control.extend({

            includes: L.Evented.prototype,

            statics: {
                Events: EVENTS
            },

            options: {
                size: [320, 320],
                position: 'topleft',
                instructionGroupsCollapsed: false,
                showInstructionGroups: true,
                imperialDistance: null
            },

            /**
             * Leaflet init method executed when adding control to map.
             *
             * Options will be stored inside control.
             *
             * @ignore
             * @method initialize
             * @param {Object} [options] Options to setup control.
             */
            initialize: function(options) {
                L.Util.setOptions(this, options);
            },

            /**
             * Leaflet method executed when adding control to map.
             * Map will be stored inside control.
             *
             * @ignore
             * @method onAdd
             * @param {L.Map} map
             * @return {HTMLElement} The main HTML element containing the widget.
             */
            onAdd: function(map) {
                this.unitService = map.getUnitService();
                this.unitService.on(L.UnitService.Events.UNIT_CHANGED, handleUnitChange, this);
                return createDOMElements(this);
            },

            onRemove: function() {
                this.unitService.off(L.UnitService.Events.UNIT_CHANGED, handleUnitChange, this);
            },

            /**
             * Shows route instructions.
             *
             * By default the instructions will be hidden.
             * Use this method to show the control after data is ready.
             *
             * @method show
             */
            show: function() {
                if (this.guidanceData) {
                    this.container.style.visibility = '';
                }
            },

            /**
             * Hides the route instructions.
             *
             * By default instructions will be hidden.
             *
             * @method hide
             */
            hide: function() {
                this.container.style.visibility = 'hidden';
            },

            /**
             * @ignore
             * First check the configuration for this instance and then the global configuration.
             * The instance configuration has priority.
             *
             * @return {Boolean}
             */
            _shouldUseImperialDistance: function() {
                if (typeof this.options.imperialDistance === 'boolean') {
                    return this.options.imperialDistance;
                }
                if (typeof this.isImperialDistance === 'boolean') {
                    return this.isImperialDistance;
                }
                return this.unitService.getCurrentUnit().getLabel() === unitTypes.imperial;
            },

            _redraw: function() {
                if (this.guidanceData) {
                    updateDOMElements(this);
                    this.show();
                } else {
                    this.hide();
                }
            },

            /**
             * Display the route instructions or update the already displayed content.
             *
             * This method should be called to update the instructions displayed after each route calculation.
             *
             * @method updateGuidanceData
             * @param {Object} guidanceData The routing properties object containing instruction groups
             *     and instructions.
             * @param {Boolean} [imperial] Display distance using imperial units
             */
            updateGuidanceData: function(guidanceData, imperial) {
                this.guidanceData = guidanceData || this.guidanceData;
                this.isImperialDistance = imperial;
                this._redraw();
            },

            /**
             * Method to show instructions with groups if there are any.
             * @method showInstructionGroups
             */
            showInstructionGroups: function() {
                if (this.guidanceData && !this.options.showInstructionGroups) {
                    this.options.showInstructionGroups = true;
                    updateDOMElements(this);
                }
            },

            /**
             * Method to hide instructions groups.
             *
             * Use this method to disable groups. Only instructions will be visible.
             * @method hideInstructionGroups
             */
            hideInstructionGroups: function() {
                if (this.guidanceData && this.options.showInstructionGroups) {
                    this.options.showInstructionGroups = false;
                    updateDOMElements(this);
                }
            },

            /**
             * Event that is fired when instruction is clicked and is not selected.
             *
             * In this case clicked instruction will
             * be marked as selected (selected-instruction css class will be added).
             * If any other instruction is selected,
             * it will be deselect.
             *
             * @event InstructionClickedSelect
             * @param {Object} point Object of point that represents latitude and longitude of instruction location
             * @param {String} message Instruction message
             * @param {String} [type="instructionClickedDeselect"] Event's name
             * @param {Element} target Event's target
             *
             * @example
             * ```javascript
             * var widget = (new L.RouteInstructions())).addTo(map);
             * widget.on(L.RouteInstructions.Events.InstructionClickedSelect, function(event) {
             *      console.log(event);
             * });
             * ```
             */
            /**
             * Event that is fired when instruction is clicked and is selected. In this case clicked instruction will
             * be deselect (selected-instruction css class will be removed).
             *
             * @event InstructionClickedDeselect
             * @param {String} [type="instructionClickedDeselect"] Event's name
             * @param {Element} target Event's target
             *
             * @example
             * ```javascript
             * var widget = (new L.RouteInstructions())).addTo(map);
             * widget.on(L.RouteInstructions.Events.InstructionClickedDeselect, function(event) {
             *      console.log(event);
             * });
             * ```
             */
            /**
             * Event that is fired when cursor is moving into instruction.
             *
             * @event InstructionHoverOn
             * @param {Object} point Object of point that represents latitude and longitude of instruction location
             * @param {String} message Instruction message
             * @param {String} [type="instructionHoverOn"] Event's name
             * @param {Element} target Event's target
             *
             * @example
             * ```javascript
             * var widget = (new L.RouteInstructions())).addTo(map);
             * widget.on(L.RouteInstructions.Events.InstructionHoverOn, function(event) {
             *      console.log(event);
             * });
             * ```
             */
            /**
             * Event that is fired when cursor is moving out of instruction.
             *
             * @event InstructionHoverOff
             * @param {String} [type="instructionHoverOff"] Event's name
             * @param {Element} target Event's target
             *
             * @example
             * ```javascript
             * var widget = (new L.RouteInstructions())).addTo(map);
             * widget.on(L.RouteInstructions.Events.InstructionHoverOff, function(event) {
             *      console.log(event);
             * });
             * ```
             */
            /**
             * Event that is fired when group of instructions is clicking and related instructions are collapsing.
             *
             * @event InstructionGroupClickedCollapse
             * @param {Object} points Object with points that represents latitude and longitude of all instruction
             * locations in route
             * @param {String} [type="instructionGroupClickedCollapse"] Event's name
             * @param {Element} target Event's target
             *
             * @example
             * ```javascript
             * var widget = (new L.RouteInstructions())).addTo(map);
             * widget.on(L.RouteInstructions.Events.InstructionGroupClickedCollapse, function(event) {
             *      console.log(event);
             * });
             * ```
             */
            /**
             * Event that is fired when group of instructions is clicking and related instructions are expanding.
             *
             * @event InstructionGroupClickedExpand
             * @param {Object} points Object with points that represents latitude and longitude of all instruction
             * locations in group
             * @param {String} [type="instructionGroupClickedExpand"] Event's name
             * @param {Element} target Event's target
             *
             * @example
             * ```javascript
             * var widget = (new L.RouteInstructions())).addTo(map);
             * widget.on(L.RouteInstructions.Events.InstructionGroupClickedExpand, function(event) {
             *      console.log(event);
             * });
             * ```
             */
            /**
             * Event that is fired when cursor is moving into group of instructions.
             *
             * @event InstructionGroupHoverOn
             * @param {Array} points Array of objects that contains points (latitude and longitude) of all instructions
             * from this group
             * @param {String} message Group message
             * @param {String} [type="instructionGroupHoverOn"] Event's name
             * @param {Element} target Event's target
             *
             * @example
             * ```javascript
             * var widget = (new L.RouteInstructions())).addTo(map);
             * widget.on(L.RouteInstructions.Events.InstructionGroupHoverOn, function(event) {
             *      console.log(event);
             * });
             * ```
             */
            /**
             * Event that is fired when cursor is moving out of group of instructions.
             *
             * @event InstructionGroupHoverOff
             * @param {String} [type="instructionGroupHoverOff"] Event's name
             * @param {Element} target Event's target
             *
             * @example
             * ```javascript
             * var widget = (new L.RouteInstructions())).addTo(map);
             * widget.on(L.RouteInstructions.Events.InstructionGroupHoverOff, function(event) {
             *      console.log(event);
             * });
             * ```
             */
            Events: EVENTS
        });

        return function(options) {
            var routeInstructions = new L.RouteInstructions(options);
            Utils.addDeprecatedProperty(routeInstructions, 'Event', 'Events');

            return routeInstructions;
        };
    });



// WEBPACK FOOTER //
// ./widgets/controls/routeInstructions.js